import type { AxiosInstance } from "axios";
import { BarChart3, ChevronRight, Info, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Card } from "../components/ui/card.js";
import { DateRangePicker } from "../components/DateRangePicker.js";
import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { daysBefore, rangeProblem, toLocalDate, type DateRange } from "../lib/dates.js";

/**
 * Campaign Stage Analysis. Ported from `ecom_code`'s `CampaignStageAnalysis.jsx` +
 * `campaignStageAnalysis.query.js` (already ClickHouse-native there, so this is a close
 * port of the SQL rather than a re-derivation from a relational schema — see
 * `packages/ch/src/reports/campaign-stage-analysis.ts`'s doc comment for the full story,
 * including the one deliberate divergence: PURCHASER is lifetime, not windowed, which
 * follows legacy's own SQL rather than its own (slightly incorrect) prose).
 *
 * WHAT IT SHOWS: campaigns grouped into funnel-intent STAGE BLOCKS (read off each
 * campaign's own objective/type, not a computed performance state), and for each block —
 * and each campaign inside it — how the visitors it reached split across New/Old ×
 * Purchaser/Non-purchaser. There is no spend, revenue, or ROAS column; this report is
 * about WHO a stage of the funnel reaches, not what it costs.
 *
 * THE STACKED COHORT BAR IS PORTED, not dropped — legacy's `CohortStackedBar` (4 flex
 * segments, SVG crosshatch/dot pattern fills, hover tooltip with the exact 4-way split)
 * is this report's own defining visual and the owner asked for it explicitly. Table shell,
 * columns, and type scale are this platform's own (`row-head`/`row-even`/`row-odd`,
 * `text-[13px] font-semibold` headers) rather than legacy's AntD `Card` stack — only the
 * BAR's own colours are fixed hex, same as legacy: they encode Blue=New / Orange=Old and
 * are not meant to follow the light/dark theme, any more than a chart's series colours
 * would. Legend text says "Orange = Old" to match `ecom_code/client/src/backend/
 * CampaignAnalytics/CampaignStageAnalysis.jsx`'s actual source (`#ea580c`/`#f97316`) —
 * if a running legacy build ever shows "Green = Old" instead, that build is stale/cached,
 * not a second source of truth.
 */

interface StageCampaign {
  readonly campaign_id: string;
  readonly campaign_name: string;
  readonly total_visitors: number;
  readonly new_visitors: number;
  readonly old_visitors: number;
  readonly new_purchasers: number;
  readonly new_non_purchasers: number;
  readonly old_purchasers: number;
  readonly old_non_purchasers: number;
  readonly purchasers: number;
  readonly purchaser_pct: number;
  readonly share_of_block: number;
}

interface StageBlock {
  readonly stage_block: string;
  readonly total_visitors: number;
  readonly new_visitors: number;
  readonly old_visitors: number;
  readonly new_purchasers: number;
  readonly new_non_purchasers: number;
  readonly old_purchasers: number;
  readonly old_non_purchasers: number;
  readonly purchasers: number;
  readonly purchaser_pct: number;
  readonly campaigns: readonly StageCampaign[];
}

interface StageResponse {
  readonly platform: "meta" | "google";
  readonly from: string;
  readonly to: string;
  readonly stageBlocks: readonly StageBlock[];
  readonly totals: {
    readonly total_visitors: number;
    readonly new_visitors: number;
    readonly old_visitors: number;
    readonly purchasers: number;
    readonly purchaser_pct: number;
  };
}

type Platform = "meta" | "google";

/** Last 30 days, matching legacy's default (`dayjs().subtract(29, 'day')` to today). */
function defaultStageRange(today: Date = new Date()): DateRange {
  return { from: toLocalDate(daysBefore(today, 29)), to: toLocalDate(today) };
}

const num = (n: number): string => (n === 0 ? "—" : n.toLocaleString("en-IN"));
const pctStr = (n: number): string => `${n.toFixed(1)}%`;

/**
 * The funnel-intent tag legacy's `getIntentTag()` derives from the block's own name —
 * a pure UI label, not a separate value from the API. Same three buckets, same fallback.
 */
function intentTag(stageBlock: string): { readonly label: string; readonly tone: "blue" | "cyan" | "emerald" | "purple" } {
  const lower = stageBlock.toLowerCase();
  if (lower.includes("awareness")) return { label: "TOF", tone: "blue" };
  if (lower.includes("traffic") || lower.includes("engagement")) return { label: "MOF", tone: "cyan" };
  if (lower.includes("sales") || lower.includes("advantage") || lower.includes("performance max")) {
    return { label: "BOF", tone: "emerald" };
  }
  return { label: "Funnel intent", tone: "purple" };
}

export function CampaignStageAnalysisScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
}): React.JSX.Element {
  const [range, setRange] = useState<DateRange>(() => defaultStageRange());
  const [platform, setPlatform] = useState<Platform>("meta");
  const [data, setData] = useState<StageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [showGuideSection, setShowGuideSection] = useState(false);
  const { colWidths, startResize } = useTableResize([320, 560]);

  const problem = useMemo(() => rangeProblem(range), [range]);

  const load = useCallback(async () => {
    if (problem !== null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{ success: boolean; data: StageResponse }>("/api/v2/campaign-stage-analysis", {
        params: { from: range.from, to: range.to, platform },
      });
      setData(res.data.data);
      // First block open on load — matches legacy.
      const first = res.data.data.stageBlocks[0];
      setExpanded(first === undefined ? new Set() : new Set([first.stage_block]));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load campaign stage analysis. Check that this platform is connected."));
      setData(null);
      setExpanded(new Set());
    } finally {
      setLoading(false);
    }
  }, [client, range.from, range.to, platform, problem]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  const toggle = (stageBlock: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stageBlock)) next.delete(stageBlock);
      else next.add(stageBlock);
      return next;
    });
  };

  const blocks = data?.stageBlocks ?? [];
  const totals = data?.totals ?? null;
  const isEmpty = !loading && blocks.length === 0;

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <BarChart3 className="text-primary size-6" />
            Campaign Stage Analysis
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
            />
          </h1>
          <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
            Campaigns grouped by funnel-intent stage, with new versus returning visitors and purchasers for each.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={range} onApply={setRange} />
          <Select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform)}
            className="h-9 w-32 text-xs font-medium"
            aria-label="Ad platform"
          >
            <option value="meta">Meta</option>
            <option value="google">Google</option>
          </Select>
        </div>
      </header>

      {/*
        Guide content mirrors legacy's inline `CampaignStageReportNotes` /
        `CampaignStageCalculationNotes` — same "Stage Blocks / Cohort Split / KPI Cards"
        and "New / Old / Purchaser / Share of Block" explanations, in this platform's own
        accordion component rather than legacy's always-visible cards.
      */}
      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              Campaign Stage Analysis groups your campaigns into <strong>funnel stage blocks</strong> and shows how the
              visitors each block reaches split between new/returning and purchaser/non-purchaser:
            </p>
            <HighlightGrid>
              <HighlightCard tone="blue" title="1. Stage Blocks" icon={<BarChart3 className="size-3.5" />}>
                Campaigns are grouped by objective/type into TOF (Awareness), MOF (Traffic/Engagement) or BOF
                (Sales/Advantage+/Performance Max) intent blocks. Click a block to expand its individual campaigns.
              </HighlightCard>
              <HighlightCard tone="purple" title="2. New vs Old, Purchaser vs Non-Purchaser" icon={<Users className="size-3.5" />}>
                Each visitor is split twice: New (first-ever session inside the selected range) or Old (returning), and
                Purchaser (has ever placed an order) or Non-Purchaser.
              </HighlightCard>
              <HighlightCard tone="emerald" title="3. KPI Cards" icon={<ShieldCheck className="size-3.5" />}>
                Total Campaign Visitors, New Visitors, Old (Returning) Visitors, and Lifetime Purchasers with conversion
                % summarise the whole selected date range and platform.
              </HighlightCard>
            </HighlightGrid>
          </div>
        }
        howTitle="How cohort segments & share are calculated"
        howContent={
          <div className={noteText}>
            <p className="m-0">How visitor cohort segments and metrics are calculated:</p>
            <FormulaList>
              <li>
                <strong>New visitors:</strong> visitors whose first-ever session with the store falls inside the
                selected date range, attributed to this campaign/stage block.
              </li>
              <li>
                <strong>Old (returning) visitors:</strong> visitors who had already visited the store before the
                selected range began, but reached this campaign/stage block during it.
              </li>
              <li>
                <strong>Purchaser vs non-purchaser:</strong> a purchaser has placed at least one order at ANY point in
                the store&apos;s history, not only inside the selected range — a repeat customer who bought before the
                window still counts.
              </li>
              <li>
                <strong>Share of block:</strong> a campaign&apos;s total visitors ÷ its stage block&apos;s total
                visitors × 100 — how much of the block&apos;s traffic came from this specific campaign.
              </li>
            </FormulaList>
          </div>
        }
      />

      {problem !== null && (
        <p className="text-destructive text-sm" role="alert">
          {problem}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={<Users className="size-4" />}
          label="Total campaign visitors"
          value={loading || totals === null ? null : num(totals.total_visitors)}
        />
        <KpiCard
          icon={<TrendingUp className="size-4" />}
          label="New visitors"
          value={loading || totals === null ? null : num(totals.new_visitors)}
        />
        <KpiCard
          icon={<BarChart3 className="size-4" />}
          label="Old (returning) visitors"
          value={loading || totals === null ? null : num(totals.old_visitors)}
        />
        <KpiCard
          icon={<ShieldCheck className="size-4" />}
          label="Lifetime purchasers"
          value={loading || totals === null ? null : num(totals.purchasers)}
          sub={loading || totals === null ? null : `${pctStr(totals.purchaser_pct)} conversion`}
        />
      </div>

      <StagePatterns />

      <PatternLegend />

      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="max-h-[min(70svh,48rem)] overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="row-head text-foreground sticky top-0 left-0 z-30 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                  Campaign
                  <TableResizeHandle onResize={(e) => startResize(0, e)} />
                </th>
                <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                  Visitor split
                  <TableResizeHandle onResize={(e) => startResize(1, e)} />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }, (_, i) => (
                  <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-52" />
                    </td>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              ) : isEmpty ? (
                <tr>
                  <td colSpan={2} className="text-muted-foreground px-4 py-14 text-center text-sm">
                    No campaigns found for {storeName} in this period and platform.
                  </td>
                </tr>
              ) : (
                blocks.map((block) => (
                  <StageBlockRows
                    key={block.stage_block}
                    block={block}
                    isOpen={expanded.has(block.stage_block)}
                    onToggle={toggle}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/**
 * The 4 colours that encode the bar, once, hidden — legacy's own `SvgPatterns`. Every
 * `CohortStackedBar` segment fills from `url(#pattern-...)`, so this only needs to exist
 * once per page, not once per bar.
 */
function StagePatterns(): React.JSX.Element {
  return (
    <svg aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
      <defs>
        <pattern id="stage-crosshatch-blue" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="8" height="8" fill="#2563eb" />
          <line x1="0" y1="0" x2="0" y2="8" stroke="#000" strokeWidth="2.5" />
          <line x1="0" y1="0" x2="8" y2="0" stroke="#000" strokeWidth="2.5" />
        </pattern>
        <pattern id="stage-dots-blue" width="9" height="9" patternUnits="userSpaceOnUse">
          <rect width="9" height="9" fill="#3b82f6" />
          <circle cx="2.5" cy="2.5" r="1.8" fill="#000" />
          <circle cx="7" cy="7" r="1.8" fill="#000" />
        </pattern>
        <pattern id="stage-crosshatch-orange" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="8" height="8" fill="#ea580c" />
          <line x1="0" y1="0" x2="0" y2="8" stroke="#000" strokeWidth="2.5" />
          <line x1="0" y1="0" x2="8" y2="0" stroke="#000" strokeWidth="2.5" />
        </pattern>
        <pattern id="stage-dots-orange" width="9" height="9" patternUnits="userSpaceOnUse">
          <rect width="9" height="9" fill="#f97316" />
          <circle cx="2.5" cy="2.5" r="1.8" fill="#000" />
          <circle cx="7" cy="7" r="1.8" fill="#000" />
        </pattern>
      </defs>
    </svg>
  );
}

const LEGEND_ITEMS: readonly { readonly pattern: string; readonly solid: string; readonly border: string; readonly label: string }[] = [
  { pattern: "stage-crosshatch-blue", solid: "#2563eb", border: "#1d4ed8", label: "New Visitors — Purchasers (Blue + Black Crosshatch)" },
  { pattern: "stage-dots-blue", solid: "#3b82f6", border: "#3b82f6", label: "New Visitors — Non-Purchasers (Blue + Black Dots)" },
  { pattern: "stage-crosshatch-orange", solid: "#ea580c", border: "#c2410c", label: "Old Visitors — Purchasers (Orange + Black Crosshatch)" },
  { pattern: "stage-dots-orange", solid: "#f97316", border: "#f97316", label: "Old Visitors — Non-Purchasers (Orange + Black Dots)" },
];

/** Ported verbatim from legacy's own legend bar — same 4 swatches, same wording. */
function PatternLegend(): React.JSX.Element {
  return (
    <div className="bg-card flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4 text-xs shadow-sm">
      <span className="text-foreground flex shrink-0 items-center gap-1.5 font-bold">
        <Info className="text-primary size-4" /> 2-Color Pattern Legend (Blue = New, Orange = Old):
      </span>
      <div className="flex flex-wrap items-center gap-6">
        {LEGEND_ITEMS.map((item) => (
          <div key={item.pattern} className="flex items-center gap-2">
            <span
              className="size-4 rounded-xs border"
              style={{ background: `url(#${item.pattern}), ${item.solid}`, borderColor: item.border }}
            />
            <span className="text-foreground font-medium">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CohortLike {
  readonly total_visitors: number;
  readonly new_purchasers: number;
  readonly new_non_purchasers: number;
  readonly old_purchasers: number;
  readonly old_non_purchasers: number;
}

const SEGMENTS: readonly {
  readonly key: keyof Omit<CohortLike, "total_visitors">;
  readonly pattern: string;
  readonly solid: string;
  readonly label: string;
}[] = [
  { key: "new_purchasers", pattern: "stage-crosshatch-blue", solid: "#2563eb", label: "New Purchasers" },
  { key: "new_non_purchasers", pattern: "stage-dots-blue", solid: "#3b82f6", label: "New Non-Purchasers" },
  { key: "old_purchasers", pattern: "stage-crosshatch-orange", solid: "#ea580c", label: "Old Purchasers" },
  { key: "old_non_purchasers", pattern: "stage-dots-orange", solid: "#f97316", label: "Old Non-Purchasers" },
];

interface TooltipCoords {
  readonly top: number;
  readonly left: number;
  readonly placement: "above" | "below";
}

/** Half the tooltip's own width (`w-64` = 256px) — used to clamp it inside the viewport. */
const TOOLTIP_HALF_WIDTH = 128;

/**
 * The 4-segment cohort bar — same shape as legacy's `CohortStackedBar`. Each segment's
 * width is its share of `total_visitors`, floored at 1.2% so a real-but-tiny segment (e.g.
 * 0.2% Old Purchasers) stays visible rather than disappearing into a sliver.
 *
 * The hover tooltip is a `createPortal`, `position: fixed` panel — same technique
 * `AppSidebar.tsx`'s `GroupFlyout` uses — NOT a CSS `group-hover` child, because a child
 * tooltip is clipped by the table's own `overflow-auto` scroll container the instant a row
 * near the top or bottom is hovered (confirmed live: the first block's tooltip rendered
 * half cut off, exactly the clipping risk called out — and dismissed as an acceptable
 * tradeoff — when this was first written; the owner hit it immediately in practice). A
 * portalled tooltip escapes that ancestor entirely and is positioned from the bar's own
 * `getBoundingClientRect()`, flipping below the bar instead of above when there is not
 * enough room above it (e.g. the very first row under a sticky header).
 */
function CohortStackedBar({ item, name, compact = false }: { readonly item: CohortLike; readonly name: string; readonly compact?: boolean }): React.JSX.Element {
  const total = item.total_visitors;
  const barRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipCoords | null>(null);

  const showTooltip = (): void => {
    const rect = barRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    const placement = rect.top < 150 ? "below" : "above";
    setTooltip({
      top: placement === "above" ? rect.top - 8 : rect.bottom + 8,
      left: Math.min(Math.max(rect.left + rect.width / 2, TOOLTIP_HALF_WIDTH + 8), window.innerWidth - TOOLTIP_HALF_WIDTH - 8),
      placement,
    });
  };
  const hideTooltip = (): void => setTooltip(null);

  if (total === 0) {
    return <div className="bg-muted text-muted-foreground flex h-6 items-center justify-center rounded-md text-[11px]">No visitors</div>;
  }

  return (
    <>
      <div
        ref={barRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className={cn("bg-muted flex overflow-hidden rounded-md shadow-inner", compact ? "h-5" : "h-7")}
      >
        {SEGMENTS.map((seg) => {
          const count = item[seg.key];
          if (count <= 0) return null;
          const pct = Math.max((count / total) * 100, 1.2);
          return (
            <div
              key={seg.key}
              className="relative h-full shrink-0"
              style={{ width: `${pct}%`, minWidth: "6px", backgroundColor: seg.solid }}
            >
              <svg className="absolute inset-0 size-full" aria-hidden="true">
                <rect width="100%" height="100%" fill={`url(#${seg.pattern})`} />
              </svg>
            </div>
          );
        })}
      </div>

      {tooltip !== null &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: tooltip.top,
              left: tooltip.left,
              transform: tooltip.placement === "above" ? "translate(-50%, -100%)" : "translate(-50%, 0)",
            }}
            className="pointer-events-none z-50 w-64 rounded-md bg-[#262626] p-2.5 text-[11px] text-white shadow-lg"
          >
            <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-white/15 pb-1 font-semibold">
              <span className="truncate">{name}</span>
              <span className="shrink-0 font-mono text-white/70">{total.toLocaleString("en-IN")} visitors</span>
            </div>
            <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1">
              {SEGMENTS.map((seg) => {
                const count = item[seg.key];
                return (
                  <Fragment key={seg.key}>
                    <span className="flex items-center gap-1.5">
                      <span className="size-2 shrink-0 rounded-xs" style={{ backgroundColor: seg.solid }} />
                      {seg.label}
                    </span>
                    <span className="text-right font-mono font-semibold">
                      {count.toLocaleString("en-IN")} ({total > 0 ? ((count / total) * 100).toFixed(1) : "0.0"}%)
                    </span>
                  </Fragment>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

const INTENT_TONE: Record<ReturnType<typeof intentTag>["tone"], string> = {
  blue: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-300",
  cyan: "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-900/50 dark:bg-cyan-950/40 dark:text-cyan-300",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300",
  purple: "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900/50 dark:bg-purple-950/40 dark:text-purple-300",
};

function StageBlockRows({
  block,
  isOpen,
  onToggle,
}: {
  readonly block: StageBlock;
  readonly isOpen: boolean;
  readonly onToggle: (stageBlock: string) => void;
}): React.JSX.Element {
  const tag = intentTag(block.stage_block);

  return (
    <>
      <tr className="bg-muted/60 border-t border-b">
        <td colSpan={2} className="px-3 py-2.5">
          <button
            type="button"
            onClick={() => onToggle(block.stage_block)}
            className="flex w-full items-center gap-4 text-left"
          >
            <span className="flex min-w-0 shrink-0 items-center gap-2.5 md:w-72">
              <ChevronRight className={cn("text-muted-foreground size-3.5 shrink-0 transition-transform", isOpen && "rotate-90")} />
              <span
                className={cn("inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-bold", INTENT_TONE[tag.tone])}
              >
                {tag.label}
              </span>
              <span className="min-w-0">
                <span className="text-foreground block truncate text-sm font-bold">{block.stage_block}</span>
                <span className="text-muted-foreground block text-[11px]">
                  {block.campaigns.length} {block.campaigns.length === 1 ? "campaign" : "campaigns"}
                </span>
              </span>
            </span>

            <span className="min-w-0 flex-1">
              <CohortStackedBar item={block} name={block.stage_block} />
            </span>

            <span className="w-20 shrink-0 text-right">
              <span className="text-foreground block text-sm font-bold tabular-nums">{num(block.total_visitors)}</span>
              <span className="text-muted-foreground block text-[11px]">visitors</span>
            </span>
          </button>
        </td>
      </tr>

      {isOpen &&
        block.campaigns.map((camp, idx) => (
          <tr key={camp.campaign_id} className={cn("transition-colors", idx % 2 === 1 ? "row-odd" : "row-even")}>
            <td className="border-b px-3 py-2.5 pl-9">
              <span className="text-foreground block max-w-[24rem] truncate text-xs font-medium" title={camp.campaign_name}>
                {camp.campaign_name}
              </span>
              <span className="text-muted-foreground block truncate font-mono text-[10px]">ID: {camp.campaign_id}</span>
            </td>
            <td className="border-b px-3 py-2.5">
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <CohortStackedBar item={camp} name={camp.campaign_name} compact />
                </div>
                <div className="w-20 shrink-0 text-right">
                  <span className="text-foreground block text-xs font-bold tabular-nums">{pctStr(camp.share_of_block)}</span>
                  <span className="text-muted-foreground block text-[10px] tabular-nums">{num(camp.total_visitors)}</span>
                </div>
              </div>
            </td>
          </tr>
        ))}
    </>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string | null;
  readonly sub?: string | null;
}): React.JSX.Element {
  return (
    <Card className="gap-2 p-4">
      <div className="text-muted-foreground flex items-center justify-between text-xs font-medium tracking-wide uppercase">
        <span>{label}</span>
        <span className="text-primary">{icon}</span>
      </div>
      {value === null ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <div className="text-foreground text-2xl font-bold tracking-tight tabular-nums">{value}</div>
      )}
      {sub !== undefined && sub !== null && <div className="text-muted-foreground text-[11px]">{sub}</div>}
    </Card>
  );
}
